
import {
  disconnectGoogle,
  getGoogleSettings,
  startGoogleAuthorize,
} from "../api/settings.js";

type GoogleHost = HTMLElement & {
  configured: boolean;
  connected: boolean;
  status: string;
  error: string;
};

async function refreshGoogle(host: GoogleHost): Promise<void> {
  try {
    const settings = await getGoogleSettings();
    host.error = "";
    host.configured = settings.configured;
    host.connected = settings.connected;
    if (!settings.configured) {
      host.status = "Google is not configured on this server.";
    } else if (settings.connected) {
      host.status = settings.email ? `Connected as ${settings.email}.` : "Google is connected.";
    } else {
      host.status = "Google is not connected.";
    }
  } catch {
    host.error = "Failed to load Google settings.";
  }
}

async function connectGoogle(host: GoogleHost): Promise<void> {
  host.error = "";
  try {
    window.location.assign(await startGoogleAuthorize(`${window.location.pathname}${window.location.search}`));
  } catch {
    host.error = "Failed to start Google sign in.";
  }
}

const styles = css`
  .status-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .status {
    color: var(--muted-foreground);
    font-size: 13px;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }
`;

export class AppSettingsGoogle extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsGoogle)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["configured","data-configured","connected","data-connected","status","data-status","error","data-error"];

  #configured = false;
  #connected = false;
  #status = "Loading...";
  #error = "";
  #configuredSet = false;
  #connectedSet = false;
  #statusSet = false;
  #errorSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get configured() {
    return this.#configured;
  }

  set configured(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#configured, next)) return;
    this.#configured = next;
    this.#configuredSet = true;
    this.#updateBindings('configured');
  }

  get connected() {
    return this.#connected;
  }

  set connected(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#connected, next)) return;
    this.#connected = next;
    this.#connectedSet = true;
    this.#updateBindings('connected');
  }

  get status() {
    return this.#status;
  }

  set status(value) {
    const raw = value ?? "Loading...";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#status, next)) return;
    this.#status = next;
    this.#statusSet = true;
    this.#updateBindings('status');
  }

  get error() {
    return this.#error;
  }

  set error(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#error, next)) return;
    this.#error = next;
    this.#errorSet = true;
    this.#updateBindings('error');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-configured')) this.configured = this.dataset.configured;
    if (this.hasAttribute('data-connected')) this.connected = this.dataset.connected;
    if (this.hasAttribute('data-status')) this.status = this.dataset.status;
    if (this.hasAttribute('data-error')) this.error = this.dataset.error;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.configured = props.configured;
      this.connected = props.connected;
      this.status = props.status;
      this.error = props.error;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#configuredSet) this.configured = this.dataset.configured;
    if (!this.#connectedSet) this.connected = this.dataset.connected;
    if (!this.#statusSet) this.status = this.dataset.status;
    if (!this.#errorSet) this.error = this.dataset.error;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void refreshGoogle(this);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'configured':
      case 'data-configured':
        this.configured = newValue;
        break;
      case 'connected':
      case 'data-connected':
        this.connected = newValue;
        break;
      case 'status':
      case 'data-status':
        this.status = newValue;
        break;
      case 'error':
      case 'data-error':
        this.error = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Google" description="Connect your Google account to give your agents native Gmail, Calendar, and Drive tools, and to trigger automations on new Gmail. Gmail is read and draft only — agents never send mail."><div class="status-row"><!--argon-text:0-->${this.#connected ? "<app-badge>Connected</app-badge>" : this.#configured ? "<app-badge variant=\"outline\">Not connected</app-badge>" : "<app-badge variant=\"secondary\">Unavailable</app-badge>"}<!--/argon-text:0--><span class="status"><!--argon-text:1-->${this.#escText(this.#status)}<!--/argon-text:1--></span></div><!--argon-text:2-->${this.#configured ? this.#connected ? `<div><app-button variant="outline" data-argon="2551">Disconnect</app-button></div>` : `<div><app-button data-argon="2963">Sign in with Google</app-button></div>` : this.#escText("")}<!--/argon-text:2--><p class="error"><!--argon-text:3-->${this.#escText(this.#error)}<!--/argon-text:3--></p></app-settings-section>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'connected' || prop === 'configured') {
      const __argonNext = `${this.#connected ? "<app-badge>Connected</app-badge>" : this.#configured ? "<app-badge variant=\"outline\">Not connected</app-badge>" : "<app-badge variant=\"secondary\">Unavailable</app-badge>"}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'status') {
      const __argonNext = `${this.#escText(this.#status)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'configured' || prop === 'connected') {
      const __argonNext = `${this.#configured ? this.#connected ? `<div><app-button variant="outline" data-argon="2551">Disconnect</app-button></div>` : `<div><app-button data-argon="2963">Sign in with Google</app-button></div>` : this.#escText("")}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2551').forEach(el => this.#listen(el, '2551', 'click', () => {
    void disconnectGoogle()
        .then(() => refreshGoogle(this))
        .catch(() => {
        this.error = "Failed to disconnect Google.";
    });
}));
    this.#eventTargets(scope, '2963').forEach(el => this.#listen(el, '2963', 'click', () => { void connectGoogle(this); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
